/**
 * Theme tokens — pure data, zero React, zero JSX.
 *
 * This file is the single source of truth for color, gradient, score
 * thresholds, and tone classes used across the PureID admin console.
 * Splitting tokens from components (theme.tsx) lets small primitives
 * like IconBadge and FieldLabel import the tokens they need without
 * creating a circular dependency with the KpiCard / SectionCard /
 * GradientDialog that also use them.
 *
 * If you only need data (constants, thresholds, class maps), import
 * from here. If you need the actual React components, import from
 * "./theme" — it re-exports everything in this file plus
 * the components themselves.
 */

// ---------------------------------------------------------------------------
// Color Convention — read this before adding new colored UI
// ---------------------------------------------------------------------------
//
// In an enterprise admin console, color is one of the most expensive
// signals available — users learn it once and then scan with it. Reusing
// the same color for two unrelated meanings on different pages costs
// trust and creates real misreads (e.g. "PCI is amber, that means
// something is wrong" — when actually it was just framework branding).
//
// Gradients and badges follow these meanings. The reason is the whole rule: users learn a
// colour once and then scan with it, so a second meaning for the same hue costs a real misread.
// Change a meaning if you have a better one, but change it everywhere at once:
//
//   GRADIENTS.success (green)  → healthy, passing, compliant, enabled,
//                                 high adoption, score >= 80
//   GRADIENTS.warning (amber)  → needs attention, partial, at-risk,
//                                 report-only, single passkey, score 50-79
//   GRADIENTS.danger  (red)    → failing, non-compliant, blocked, critical,
//                                 no passkeys, score < 50
//   GRADIENTS.brand   (indigo) → the DEFAULT accent for anything without a
//                                 health/state meaning — the brand voice.
//   GRADIENTS.info    (blue)   → an explicit informational CALLOUT only
//                                 (e.g. AlertBanner "info"). NOT the default.
//   GRADIENTS.slate   (gray)   → not applicable, inactive, unknown, stale
//
// DEFAULT IS THE BRAND ACCENT. If a color has no specific meaning, use GRADIENTS.brand
// (or tone="brand"). It's derived from --primary, so it matches the brand mark and
// every interactive control and flips light↔dark automatically — non-semantic cards
// never compete with the brand. Reserve success/warning/danger for real state, and
// info-blue for explicit informational callouts. Protocol type (OIDC vs SAML), app
// kind, webhook family, etc. are NOT worth spending a color on — show them in a
// neutral Badge or subtitle and keep the accent the brand. Two adjacent cards with
// different non-semantic colors is a bug, not a feature.
//
// Score-based colors: ALWAYS use gradientForScore() / toneForScore()
// below — never inline the thresholds. We had three pages with three
// slightly different cutoffs (75 vs 80 for "good") and it confused users.

export const GRADIENTS = {
  // Brand accent — the DEFAULT decorative one (card/table/dialog top bars, icon
  // wells). Derived from --primary via arbitrary values so it flips light↔dark with
  // the theme and always matches the brand mark + interactive controls.
  brand: "from-[hsl(var(--primary))] to-[hsl(var(--primary)/0.55)]",
  // 2026-08-07: every gradient now derives from its token, the way `brand` always did. These
  // were hardcoded raw-palette shades, which meant the "single source of truth for color" did
  // not move when a token moved — half a repaint, every time. Same visual ramp, expressed as
  // the token at full strength fading to 55%, so light↔dark flips come free.
  info: "from-[hsl(var(--info))] to-[hsl(var(--info)/0.55)]",
  success: "from-[hsl(var(--success))] to-[hsl(var(--success)/0.55)]",
  warning: "from-[hsl(var(--warning))] to-[hsl(var(--warning)/0.55)]",
  danger: "from-[hsl(var(--danger))] to-[hsl(var(--danger)/0.55)]",
  slate: "from-[hsl(var(--muted-foreground))] to-[hsl(var(--muted-foreground)/0.55)]",
} as const;

// ---------------------------------------------------------------------------
// Score → Gradient mapping (the single source of truth for health scores)
// ---------------------------------------------------------------------------
//
// Use this for any 0–100 health/readiness/posture/coverage score that
// should be visualized with the standard green/amber/red severity scale.
// Thresholds are deliberately fixed here so that "what counts as healthy"
// stays the same across pages — readiness, credential health, app
// posture, migration progress, and compliance must all use these helpers.
//
// Thresholds (industry-standard for security posture dashboards):
//   >= 80   excellent / healthy   (green)
//   >= 50   needs attention       (amber)
//   <  50   critical              (red)
//
// If you need a *different* scale (e.g. percent migrated where 50 is
// already "good enough"), do NOT lower these thresholds — define a new
// helper with a different name and document why.

export const SCORE_THRESHOLDS = {
  good: 80,
  fair: 50,
} as const;

export function gradientForScore(score: number): string {
  if (score >= SCORE_THRESHOLDS.good) return GRADIENTS.success;
  if (score >= SCORE_THRESHOLDS.fair) return GRADIENTS.warning;
  return GRADIENTS.danger;
}

// Coordinated set of all color forms for a score, for UIs that need
// gradient + SVG stroke + text + background glow + progress-bar fill
// to all change together (e.g. the readiness gauge on passkey-intelligence).
//
// Tailwind requires literal class names for purging, so every variant is
// spelled out here rather than built from a base color string.
export interface ScoreColorClasses {
  gradient: string;    // token-derived "from-… to-…" — for top accent bars
  bgGradient: string;  // "bg-gradient-to-r ..." — for background glow div
  stroke: string;      // token-derived stroke — for SVG circle
  text: string;        // the role's ink token — for inline numbers
  bg: string;          // the role's mark token — for solid progress bar fill
}

// 2026-08-07: every value derives from a token. These were hardcoded raw-palette shades, so a
// score gauge kept its old colors after a token change and drifted out of step with the Badge
// beside it. `text` uses the role's INK, never its mark value — see the ink note in styles.css.
export function colorClassesForScore(score: number): ScoreColorClasses {
  if (score >= SCORE_THRESHOLDS.good) {
    return {
      gradient: GRADIENTS.success,
      bgGradient: "bg-gradient-to-r from-[hsl(var(--success))] to-[hsl(var(--success)/0.55)]",
      stroke: "stroke-[hsl(var(--success))]",
      text: "text-success-ink",
      bg: "bg-success",
    };
  }
  if (score >= SCORE_THRESHOLDS.fair) {
    return {
      gradient: GRADIENTS.warning,
      bgGradient: "bg-gradient-to-r from-[hsl(var(--warning))] to-[hsl(var(--warning)/0.55)]",
      stroke: "stroke-[hsl(var(--warning))]",
      text: "text-warning-ink",
      bg: "bg-warning",
    };
  }
  return {
    gradient: GRADIENTS.danger,
    bgGradient: "bg-gradient-to-r from-[hsl(var(--danger))] to-[hsl(var(--danger)/0.55)]",
    stroke: "stroke-[hsl(var(--danger))]",
    text: "text-danger-ink",
    bg: "bg-danger",
  };
}

// ---------------------------------------------------------------------------
// Chart colors — single source of truth for Recharts <Cell fill> / <Bar fill>
// ---------------------------------------------------------------------------
//
// Recharts and other SVG-based chart libraries can't consume Tailwind classes
// directly — they need raw color values for their `fill` / `stroke` props.
// Without a central constant, every chart consumer hardcoded its own hex
// strings, leading to three places where green = #22c55e and at least one
// place where green = #16a34a.
//
// Each value is a CSS custom property reference with a hex fallback. The CSS
// variables (defined in packages/ui/src/styles.css — NOT globals.css, which does
// not exist and never did) automatically swap between light and dark mode, so
// charts get free dark-mode adaptation.

export const CHART_COLORS = {
  // The var() is the source of truth (flips light↔dark automatically); the hex is a
  // no-CSS fallback and mirrors the light-mode token of the same name.
  //
  // "Mirrors" was an ASPIRATION, not a fact, until 2026-08-10. FOUR of the five had
  // drifted from the tokens they claim to copy — success was #207e59 against a real
  // #1B7953, danger #c72933 against #C11F2A, info #1767cf against #0A6599, and
  // neutral was a raw Tailwind grey (#6b7280) against a tinted #3F3D57. A fallback
  // only renders when the custom property is missing, so nothing ever looked wrong
  // in the app and the drift was invisible for as long as it existed.
  //
  // theme-tokens.test.ts now parses styles.css and fails if any of these stops
  // matching its token. Do not hand-edit these values — take them from that test's
  // output, which is the same discipline email_brand_test.go applies to the Go copy.
  // brand — the DEFAULT series colour for anything without a health signal, and the
  // one most likely to be hand-rolled: three chart components carried
  // `var(--color-primary, #0d8ca6)` with a TEAL fallback, the brand from two changes
  // before indigo. It survived teal -> peacock -> purple -> indigo untouched because
  // a fallback never renders while the stylesheet is present.
  brand:   "var(--color-primary, #433ACB)",
  success: "var(--color-success, #1B7953)",
  warning: "var(--color-warning, #B97D04)",
  danger:  "var(--color-danger,  #C11F2A)",
  info:    "var(--color-info,    #0A6599)",
  neutral: "var(--color-muted-foreground, #3F3D57)",
  // muted — the recessive fill behind a hovered bar / tooltip cursor.
  mutedSurface: "var(--color-muted, #E9E9F1)",
  // surface — the 2px ring drawn around an active dot so overlapping marks stay
  // separable. It is the PAGE ground, not the series colour.
  surface: "var(--color-background, #F0F0F5)",
} as const;

// Chart typography — the ONE documented exception to the 13px UI type floor.
// Data-viz axis ticks are the single place where the chrome floor doesn't apply:
// a time-series X-axis at 13px overlaps and drops ticks, losing information. So
// axis ticks/labels use a shared 12px token (never the 9–11px soup that used to
// be hardcoded per chart), and tooltips — read closely on hover, no overlap
// constraint — sit at the 13px floor. Every Recharts `tick`/`label`/`contentStyle`
// fontSize references these, so the exception lives in one place rather than drifting into a
// per-chart soup of 9 to 11px values, which is what it replaced.
export const CHART_AXIS_FONT_SIZE = 12;
export const CHART_TOOLTIP_FONT_SIZE = 13;

// ---------------------------------------------------------------------------
// Tones — semantic → Tailwind class map (the "one place where we decide
// what blue looks like")
// ---------------------------------------------------------------------------
//
// Any time a consumer needs an icon well, a soft-tinted pill, or a
// tone-colored foreground on an icon, it pulls classes from here — otherwise the pairs drift,
// which is what this map was created to stop (about 60 pages had hand-rolled their own).
// Before this existed, ~60 pages hand-rolled their own tinted-well + icon-color pairs from raw
// palette shades, with slightly different sizes, radiuses, and dark-mode opacities.
//
// Tones follow the same convention as GRADIENTS: `brand` (indigo, and it reads from
// --primary, so it followed the teal → peacock → purple → indigo moves without edits) is the default
// for anything without a health signal. Use success/warning/danger ONLY when the
// color is communicating state the user should act on, and `info` (blue) only for
// an explicit informational callout — never as the default.

export type Tone = "brand" | "info" | "success" | "warning" | "danger" | "neutral";

export interface ToneClasses {
  /** Soft tinted background for icon wells, pill bodies, etc. */
  bg: string;
  /** Border for outlined tone surfaces. */
  border: string;
  /** Foreground color for icons and text sitting on the tinted bg. */
  fg: string;
  /** Solid foreground color for inline numbers/text outside a tinted bg. */
  text: string;
  /** Gradient string from GRADIENTS (for top accent bars). */
  gradient: string;
}

// Tints + icons pull from the semantic state tokens (--success/--warning/
// --danger/--info, which flip for dark mode) — the SAME source the Badge
// and AlertBanner primitives use, so an IconBadge well / pill matches a
// Badge of the same state on the page. (Previously these were raw palette shades, so the
// greens didn't match.)
//
// `text` is BARE text on the card/page background, not foreground-on-a-tint. The solid fill
// tokens are calibrated to sit UNDER their *-foreground, so as standalone text several of them
// fail: measured on the page background, --success is 4.44:1 and --warning 3.13:1.
//
// 2026-08-07: this used to be solved with hardcoded raw-palette shades, which bypassed the theme,
// the one thing this file exists to prevent. Every state
// now ships an INK token instead, so `text` is `text-<role>-ink` with no raw palette anywhere and
// no exception to remember. `fg` (icon on the tint) keeps the mark token — that is the Badge
// context, where a mark is what it should be.
export const TONES: Record<Tone, ToneClasses> = {
  brand: {
    bg: "bg-primary/10 dark:bg-primary/15",
    border: "border-primary/20 dark:border-primary/25",
    fg: "text-primary",
    text: "text-primary",
    gradient: GRADIENTS.brand,
  },
  info: {
    bg: "bg-info/10 dark:bg-info/15",
    border: "border-info/20 dark:border-info/25",
    fg: "text-info",
    text: "text-info",
    gradient: GRADIENTS.info,
  },
  success: {
    bg: "bg-success/10 dark:bg-success/15",
    border: "border-success/20 dark:border-success/25",
    fg: "text-success",
    text: "text-success-ink",
    gradient: GRADIENTS.success,
  },
  warning: {
    bg: "bg-warning/10 dark:bg-warning/15",
    border: "border-warning/20 dark:border-warning/25",
    fg: "text-warning",
    text: "text-warning-ink",
    gradient: GRADIENTS.warning,
  },
  danger: {
    bg: "bg-danger/10 dark:bg-danger/15",
    border: "border-danger/20 dark:border-danger/25",
    fg: "text-danger",
    text: "text-danger-ink",
    gradient: GRADIENTS.danger,
  },
  neutral: {
    bg: "bg-muted/60 dark:bg-muted/40",
    border: "border-border",
    fg: "text-muted-foreground",
    text: "text-muted-foreground",
    gradient: GRADIENTS.slate,
  },
};

/** Map a 0-100 score to the corresponding tone. Shares thresholds with gradientForScore. */
export function toneForScore(score: number): Tone {
  if (score >= SCORE_THRESHOLDS.good) return "success";
  if (score >= SCORE_THRESHOLDS.fair) return "warning";
  return "danger";
}

// ---------------------------------------------------------------------------
// Internal accent-bar height constants — consumed only by the theme
// components (KpiCard, SectionCard, GradientDialog). There is no public
// helper for rolling your own accent bar: use SectionCard / GradientDialog.
// ---------------------------------------------------------------------------

export const ACCENT_BAR_CARD = "h-0.5";  // 2px — cards and tables
export const ACCENT_BAR_DIALOG = "h-1";  // 4px — dialogs
